import { apiService } from '../../../shared/services/apiService';

interface Secao {
    id: number;
    nome: string;
    descricao: string;
}

export interface PrecoFrenteCaixa {
    produtoId: number;
    nome: string;
    vlPreco: number;
    vlPrecoNormal: number;
    promocao: boolean;
    tipoUnidade: string;
    codigoBarras?: string;
    vlAtacadoNivel1?: number;
    qtAtacadoNivel1?: number;
    vlAtacadoNivel2?: number;
    qtAtacadoNivel2?: number;
    vlPrecoClienteFidelizado?: number;
    qtMinimaPrecoClienteFidelizado?: number;
    tipoCartaoPrecoClienteFidelizado?: number;
    campanhaId?: number;
    campanha?: string;
    dtFinalCampanha?: string;
    qtMaximaPorVenda?: number;
    tipoCampanha?: string;
    percentualCashBack?: number;
    dtPrecoAlterado?: string;
    vinculadoATela?: boolean;
    // Campos legados para compatibilidade
    descricao?: string;
    preco?: number;
    precoPromocional?: number;
    emPromocao?: boolean;
    departamentoId?: number;
    secaoId?: number;
}

export interface ListarPrecosResult {
    hasNext: boolean;
    items: PrecoFrenteCaixa[];
}

export interface Categoria {
    departamentoId: number;
    departamento: string;
    secaoId: number;
    secao: string;
    itensEmPromocao: number;
    itensTotal: number;
}

class PriceService {
    async listarSecoes(): Promise<Secao[]> {
        return apiService.get<Secao[]>('/v1/precos/frentecaixa/secoes');
    }

    async listarCategorias(): Promise<Categoria[]> {
        const response = await apiService.get<any[]>('/v1/precos/frentecaixa/secoes');

        // Mapear resposta da API para o formato esperado
        // Usar apenas o campo "secao" do JSON retornado
        return response.map((item: any) => ({
            departamentoId: item.departamentoId || 0,
            departamento: item.departamento || '',
            secaoId: item.secaoId || 0,
            secao: item.secao || '',
            itensEmPromocao: item.itensEmPromocao || 0,
            itensTotal: item.itensTotal || 0,
        }));
    }

    async listarPrecos(params?: {
        pesquisa?: string;
        somenteComPromocoes?: boolean;
        secaoId?: number;
        produtoId?: number;
        codigoBarras?: string;
        tiposUnidade?: string[]; // Array de tipos de unidade (siglas em maiúsculo)
        page?: number;
        pageSize?: number;
    }): Promise<ListarPrecosResult> {
        const queryParams = new URLSearchParams();
        if (params?.pesquisa) queryParams.append('Pesquisa', params.pesquisa);
        if (params?.somenteComPromocoes) queryParams.append('SomenteComPromocoes', 'true');
        if (params?.secaoId) queryParams.append('SecaoId', params.secaoId.toString());
        if (params?.produtoId) queryParams.append('ProdutoId', params.produtoId.toString());
        if (params?.codigoBarras) queryParams.append('CodigoBarras', params.codigoBarras);
        // Adicionar múltiplos parâmetros TipoUnidade (um para cada tipo selecionado)
        if (params?.tiposUnidade && params.tiposUnidade.length > 0) {
            params.tiposUnidade.forEach(tipo => {
                queryParams.append('TipoUnidade', tipo.toUpperCase()); // Garantir maiúsculo
            });
        }
        if (params?.page) queryParams.append('Page', params.page.toString());
        if (params?.pageSize) queryParams.append('PageSize', params.pageSize.toString());

        const response = await apiService.get<any>(`/v1/banner/precos?${queryParams.toString()}`);

        // Mapear resposta da API para o formato esperado
        return {
            hasNext: response.hasNext || false,
            items: response.items || []
        };
    }

    // Buscar produtos específicos por IDs (otimizado para buscar múltiplos produtos de uma vez)
    // Como a API não tem endpoint direto para buscar por IDs, vamos buscar em páginas
    // e filtrar os produtos necessários
    async buscarProdutosPorIds(produtoIds: number[]): Promise<PrecoFrenteCaixa[]> {
        if (produtoIds.length === 0) return [];

        const produtoIdsSet = new Set(produtoIds);
        const foundItems: PrecoFrenteCaixa[] = [];
        let currentPage = 1;
        const pageSize = 100;
        let hasMore = true;
        const maxPages = 10; // Limite de segurança para evitar loop infinito

        // Buscar produtos página por página até encontrar todos ou atingir o limite
        while (hasMore && foundItems.length < produtoIds.length && currentPage <= maxPages) {
            try {
                const result = await this.listarPrecos({
                    page: currentPage,
                    pageSize: pageSize
                });

                // Filtrar apenas os produtos que estão na lista de IDs procurados
                const filtered = result.items.filter(item => produtoIdsSet.has(item.produtoId));
                foundItems.push(...filtered);

                // Se encontramos todos os produtos, parar
                if (foundItems.length >= produtoIds.length) {
                    break;
                }

                hasMore = result.hasNext;
                currentPage++;
            } catch (error) {
                break;
            }
        }

        return foundItems;
    }
}

export const priceService = new PriceService();
export type { Secao };
